import { useTranslation } from "react-i18next";
import { LoadingState } from "../states";
import placeholder from "../states/Placeholder.module.css";
import { PlaceholderText } from "../states/PlaceholderText";
import heroStyles from "../consumption/UsageHero.module.css";
import panelStyles from "../consumption/ConsumptionSummary.module.css";
import usageLoading from "../consumption/UsageLoading.module.css";
import { BreakdownsLoading, BudgetsLoading, WorthALookLoading } from "../organisation/OrganisationLoading";
import gaugeStyles from "./BudgetGauge.module.css";
import styles from "./Overview.module.css";

/** Sample figures of about the length of the organisation's, which come from the data rather than the translations. */
const sample = { spend: "$59,825", people: "627", percent: "91%", amount: "$318,133", total: "$323,129", month: "October", period: "October 2026", unit: "Product & Technology" };

/**
 * A panel sketched: its title as it will read, the line under it as a soft bar, then the place its content will take.
 *
 * @param props.title - The panel's title.
 * @param props.subtitle - Sample text the length of the line under the title.
 * @param props.wide - Whether the panel spans both columns, as the spend chart does.
 * @param props.children - The content's place.
 */
function PlaceholderPanel({ title, subtitle, wide = false, children }: { title: string; subtitle: string; wide?: boolean; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className={wide ? `${panelStyles.forecastPanel} ${styles.wide}` : panelStyles.forecastPanel} aria-hidden="true">
      <p className={`panel-title ${panelStyles.panelTitle}`}>{title}</p>
      <p className={styles.subtitle}>
        <PlaceholderText>{subtitle}</PlaceholderText>
      </p>
      {children}
    </div>
  );
}

/**
 * The rows of a ranked or per-tool list, sketched: a name and a figure over a bar, row after row.
 *
 * @param props.rows - How many rows.
 */
function PlaceholderRows({ rows }: { rows: number }): React.JSX.Element {
  return (
    <div className={styles.ranking}>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index}>
          <div className={styles.rankHead}>
            <PlaceholderText>{sample.unit}</PlaceholderText>
            <PlaceholderText>{sample.spend}</PlaceholderText>
          </div>
          <div className={`${placeholder.block} ${styles.placeholderBar}`} />
        </div>
      ))}
    </div>
  );
}

/**
 * The Organization page while its figures load: the hero built from the same parts as the loaded one, with the spinner where the
 * spend will be and the budget gauges, the "worth a look" list and the figures drawn as soft shapes, then the Budgets section, the
 * trends and the breakdowns sketched in their places, so the figures arrive where they will stay. When the figures could not be
 * loaded, the same layout holds the error in place of the spinner and stops pulsing, so a retry does not swap the page between
 * two layouts.
 *
 * @param props.withPeople - Whether the page will show the people section (it names individuals, so not everyone sees it).
 * @param props.error - The error panel to show instead of the spinner, or nothing while loading.
 */
export function OverviewLoading({ withPeople, error }: { withPeople: boolean; error?: React.ReactNode }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div aria-busy={error ? undefined : true} className={error ? placeholder.still : undefined}>
      <section className={heroStyles.hero} aria-label={t("overview.hero.label")}>
        <div className={heroStyles.main}>
          <div className={heroStyles.headerRow}>
            <p className={`panel-title ${heroStyles.eyebrow}`}>{t("overview.hero.eyebrow")}</p>
            <p className={heroStyles.periodRange} aria-hidden="true">
              <PlaceholderText>{sample.period}</PlaceholderText>
            </p>
          </div>
          {error ? (
            <div className={usageLoading.error}>{error}</div>
          ) : (
            <>
              <div className={heroStyles.amount}>
                <span className={usageLoading.amountSlot}>
                  <span className={usageLoading.amountStatus}>
                    <LoadingState label={t("overview.loading")} />
                  </span>
                </span>
              </div>
              <p className={heroStyles.summary} aria-hidden="true">
                <PlaceholderText>{t("overview.hero.runningChange", { projected: sample.spend, percent: 16, direction: t("overview.hero.less"), previous: "September" })}</PlaceholderText>
              </p>
              <div className={styles.budget} aria-hidden="true">
                <div className={styles.gauges}>
                  <div className={`${gaugeStyles.gauge} ${styles.placeholderGauge} ${placeholder.pulse}`} />
                  <div className={`${gaugeStyles.gauge} ${styles.placeholderGauge} ${placeholder.pulse}`} />
                </div>
              </div>
            </>
          )}
        </div>

        <dl className={heroStyles.stats} aria-hidden="true">
          <div className={`${heroStyles.stat} ${heroStyles.statFeatured}`}>
            <dt>
              <PlaceholderText>{t("overview.hero.people")}</PlaceholderText>
            </dt>
            <dd>
              <PlaceholderText>{sample.people}</PlaceholderText>
            </dd>
          </div>
          <div className={heroStyles.stat}>
            <dt>
              <PlaceholderText>{t("overview.hero.returning", { previous: "September" })}</PlaceholderText>
            </dt>
            <dd>
              <PlaceholderText>{sample.percent}</PlaceholderText>
            </dd>
          </div>
          <div className={heroStyles.stat}>
            <dt>
              <PlaceholderText>{t("overview.hero.topTenth")}</PlaceholderText>
            </dt>
            <dd>
              <PlaceholderText>{sample.percent}</PlaceholderText>
            </dd>
          </div>
          <div className={`${heroStyles.stat} ${styles.statFull}`}>
            <dt>
              <PlaceholderText>{t("overview.hero.financialYear", { name: "FY27" })}</PlaceholderText>
            </dt>
            <dd>
              <PlaceholderText>{sample.amount}</PlaceholderText>
            </dd>
          </div>
        </dl>
        {!error && <WorthALookLoading />}
      </section>

      <BudgetsLoading />

      <div className={`${styles.grid} ${styles.trends}`}>
        <PlaceholderPanel title={t("overview.spend.title")} subtitle={t("overview.spend.subtitle", { total: sample.total, count: 12 })} wide>
          <div className={`${placeholder.block} ${styles.placeholderChart}`} />
        </PlaceholderPanel>
        <PlaceholderPanel title={t("overview.people.title")} subtitle={t("overview.people.subtitle")}>
          <div className={`${placeholder.block} ${styles.placeholderChart}`} />
        </PlaceholderPanel>
        <PlaceholderPanel title={t("overview.seats.title")} subtitle={t("overview.seats.subtitle", { month: sample.month })}>
          <PlaceholderRows rows={4} />
        </PlaceholderPanel>
      </div>

      <BreakdownsLoading withPeople={withPeople} />
    </div>
  );
}
